/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { css } from 'aphrodite';
import { mixins, colors, LoadingSpinner } from '@blend/consumer-components';
import { ConsumerAppConfigContext } from '../../../contexts/appConfig';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { useConsumerTheme } from '../../../hooks/useConsumerTheme';
import { mediaOrientations } from '../../../common/responsive';
import { MemoizedParticipant } from '../Participant/Participant';
import { useParticipantTracks } from '../twilio/hooks/useParticipantTracks';
import { ParticipantsNameTag } from './ParticipantsNameTag';
import { diagnostics } from '@utils/diagnostics';
import { isEqual } from '@utils/lodash';

export const SHOW_NAME_TAG_TIMEOUT = 6000;
export namespace ParticipantsTab {
  export interface Props {}
}

const generateStyles = (appConfig: ConsumerAppConfigContext) => ({
  participantsTabWrapper: {
    display: 'flex',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: appConfig.colors.blackPearl,
  },

  participantsWrapper: {
    display: 'grid',
    gridGap: 8,
    alignItems: 'center',
    [mediaOrientations.portrait]: {
      ...mixins.marginHorizontal(15),
      gridTemplateColumns: 'repeat(2, auto)',
    },
    [mediaOrientations.landscape]: {
      ...mixins.marginHorizontal(30),
      gridTemplateColumns: 'repeat(4, auto)',
    },
  },

  participantWrapper: {
    [mediaOrientations.landscape]: {
      width: '20vw',
    },
  },
});

// TODO: (ZOOMSDK) - add support for zoom
export const ParticipantsTab: React.FC<ParticipantsTab.Props> = ({}) => {
  const users = useRootSelector(state => state.signingRoom.users);
  const { styles } = useConsumerTheme(generateStyles);
  const { isLoading, pinnedParticipants, nonPinnedParticipants } = useParticipantTracks();
  const [nameTags, setNameTags] = useState<ParticipantsNameTag.Props['nameTags'] | undefined>(undefined);
  const timerEl = useRef<any>(null);

  // TODO: handle 8+ participants
  const showedParticipants = useMemo(() => nonPinnedParticipants.slice(0, 6), [nonPinnedParticipants]);

  const pinnedParticipantsProps = useMemo(
    () =>
      pinnedParticipants.map(participant => ({
        participant,
        pinned: true,
        dataIt: 'Participant-Pinned',
      })),
    [pinnedParticipants],
  );
  const showedParticipantsProps = useMemo(
    () =>
      showedParticipants.map(participant => ({
        participant,
        pinned: false,
        dataIt: 'Participant-nonPinned',
      })),
    [showedParticipants],
  );
  const participantsProps = [...pinnedParticipantsProps, ...showedParticipantsProps];
  const handleParticipantClick = useCallback(
    participant => {
      const userSession = users?.inVideoCall.find(user => user.id === participant.identity);
      if (!!userSession) {
        const newNameTags = userSession.users.map(user => ({
          title: user.title,
          name: `${user.firstName} ${user.lastName}`,
        }));
        if (!isEqual(nameTags, newNameTags)) {
          window.clearTimeout(timerEl.current);
          setNameTags(newNameTags);
          // hides name tags after showing for 6s
          timerEl.current = window.setTimeout(() => setNameTags(undefined), SHOW_NAME_TAG_TIMEOUT);
        }
      } else {
        diagnostics.warn(`Failed to get user session identity`);
      }
    },
    [users?.inVideoCall, nameTags],
  );

  useEffect(() => {
    return () => window.clearTimeout(timerEl.current);
  }, []);

  if (isLoading) {
    return (
      <div className={css(styles.participantsTabWrapper)} data-it="LoadingParticipantsTab">
        <LoadingSpinner size="sm" color={colors.catalyst} />
      </div>
    );
  }
  return (
    <div className={css(styles.participantsTabWrapper)} data-it="ParticipantsTab">
      <div className={css(styles.participantsWrapper)}>
        {participantsProps.map(participantProps => (
          <div className={css(styles.participantWrapper)} key={participantProps.participant.identity}>
            <MemoizedParticipant
              users={users}
              onClick={handleParticipantClick}
              participant={participantProps.participant}
              pinned={participantProps.pinned}
              data-it={participantProps.dataIt}
            />
          </div>
        ))}
      </div>
      {nameTags && <ParticipantsNameTag nameTags={nameTags} data-it="ParticipantsNameTag" />}
    </div>
  );
};
